// btw-front/src/pages/PartnerDealNew.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message, DatePicker } from "antd";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/ko_KR";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Input } from "../components/ui/Input";
import { CompanySearchSelect } from "../components/ui/CompanySearchSelect";
import { Textarea } from "../components/ui/Textarea";
import { usePartnerSession } from "../context/PartnerSessionContext";
import { create as createDeal, getMe } from "../api/partnerDeals";
import { listSalesReps } from "../api/partnerReps";
import { formatPhone, formatAmount, parseAmount, formatQuarter } from "../lib/format";
import { useDealRegistrationGate } from "../lib/dealRegistrationGate";

// yyyy-MM-dd → "yyyyQn" (1~4). 빈 문자열 / 잘못된 입력 시 "" 반환.
function quarterFromDate(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return "";
  const m = yyyyMmDd.match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (!m) return "";
  const month = Number(m[2]);
  if (month < 1 || month > 12) return "";
  const q = Math.floor((month - 1) / 3) + 1;
  return `${m[1]}Q${q}`;
}

function emptyForm() {
  return {
    projectName: "",
    customerCompany: "",
    customerBusinessNumber: "",
    customerContactName: "",
    customerContactPosition: "",
    customerPhone: "",
    customerEmail: "",
    partnerSalesRepId: "",
    poExpectedDate: "",
    productDetail: "",
    poAmount: 0,
    vatExcluded: true,
    salesProgress: "",
    channel: "",
    pocRequired: "undecided",
    brochureDelivered: false,
  };
}

export default function PartnerDealNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = usePartnerSession();

  const [form, setForm] = useState(() => emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [salesReps, setSalesReps] = useState([]);
  const [salesRepsLoading, setSalesRepsLoading] = useState(true);
  const { showBlockedNotice } = useDealRegistrationGate();

  // URL 직접 진입·오래된 세션 대비 — 서버의 최신 권한을 확인해 제한이면 안내 후 대시보드로.
  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((me) => {
        if (cancelled || !me || !me.partner) return;
        if (me.partner.canRegisterDeal === false) {
          showBlockedNotice();
          navigate("/partners/dashboard", { replace: true });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // showBlockedNotice 는 매 렌더 새로 생성되므로 의존성에서 제외(마운트 1회만 확인)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    listSalesReps()
      .then((rows) => {
        if (cancelled) return;
        setSalesReps(rows);
        // 기본 선택은 목록 첫 행(정렬 순서 기준) — 가입 때 등록된 본인 행이 보통 맨 앞이다
        const def = rows[0];
        if (def) setForm((f) => ({ ...f, partnerSalesRepId: def.id }));
      })
      .catch(() => {
        if (!cancelled) setSalesReps([]);
      })
      .finally(() => {
        if (!cancelled) setSalesRepsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedRep = salesReps.find((r) => String(r.id) === String(form.partnerSalesRepId));

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const autoQuarter = quarterFromDate(form.poExpectedDate);

  async function handleSubmit(ev) {
    ev.preventDefault();
    if (submitting) return;
    if (
      !form.projectName ||
      !form.customerCompany ||
      !form.customerContactName ||
      !form.partnerSalesRepId
    ) {
      message.warning(t("partners.signup.errors.required"));
      return;
    }
    setSubmitting(true);
    try {
      // 분기는 프로젝트 날짜에서 자동 계산해 서버로 전송. 영업대표는 id 만 보내고 서버가 스냅샷 복사.
      await createDeal({ ...form, poExpectedQuarter: autoQuarter });
      // 파트너 등록은 즉시 활성이 아니라 심사 대기다 → "등록됐다" 가 아니라 "심사 요청됐다".
      // 바로 딜 목록으로 이동하는데 거기엔 아직 안 보이므로, 왜 없는지도 함께 알린다.
      message.success(t("partners.deals.form.reviewRequested"));
      navigate("/partners/dashboard", { replace: true });
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("partners.deals.form.createFailed");
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-4xl">
            <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
              {t("partners.dashboard.addDeal")}
            </h1>

            <form className="mt-6 grid gap-6" onSubmit={handleSubmit} noValidate>
              <Section title={t("partners.deals.form.projectName")}>
                <Input
                  label={t("partners.deals.form.projectName")}
                  required
                  value={form.projectName}
                  onChange={(e) => setField("projectName", e.target.value)}
                  maxLength={200}
                />
              </Section>

              <Section title={t("partners.deals.form.sectionCustomer")}>
                <div className="grid gap-4 md:grid-cols-2">
                  <CompanySearchSelect
                    label={t("partners.deals.form.customerCompany")}
                    required
                    alertOnTaken
                    value={form.customerCompany}
                    onChange={(name) => setField("customerCompany", name)}
                    businessNumber={form.customerBusinessNumber}
                    onBusinessNumberChange={(v) => setField("customerBusinessNumber", v)}
                  />
                  <Input
                    label={t("partners.deals.form.customerContactName")}
                    required
                    value={form.customerContactName}
                    onChange={(e) => setField("customerContactName", e.target.value)}
                    maxLength={50}
                  />
                  <Input
                    label={t("partners.deals.form.customerContactPosition")}
                    value={form.customerContactPosition}
                    onChange={(e) => setField("customerContactPosition", e.target.value)}
                    maxLength={100}
                  />
                  <Input
                    label={t("partners.deals.form.customerPhone")}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    value={form.customerPhone}
                    onChange={(e) => setField("customerPhone", formatPhone(e.target.value))}
                    maxLength={13}
                  />
                  <Input
                    label={t("partners.deals.form.customerEmail")}
                    type="email"
                    value={form.customerEmail}
                    onChange={(e) => setField("customerEmail", e.target.value)}
                    maxLength={120}
                  />
                </div>
              </Section>

              <Section title={t("partners.deals.form.sectionPartner")}>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label={t("partners.deals.form.partnerCompany")}
                    value={profile.company || ""}
                    disabled
                    tabIndex={-1}
                    inputClassName="bg-slate-100 cursor-not-allowed"
                  />
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-slate-700">
                      {t("partners.deals.form.salesRepName")}{" "}
                      <span className="text-rose-500">*</span>
                    </label>
                    <select
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-accent focus:ring-2 focus:ring-accent/30"
                      value={form.partnerSalesRepId}
                      onChange={(e) => setField("partnerSalesRepId", e.target.value)}
                      disabled={salesRepsLoading || salesReps.length === 0}
                    >
                      <option value="">{t("partners.deals.form.salesRepSelect")}</option>
                      {salesReps.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                          {r.phone ? ` · ${r.phone}` : ""}
                        </option>
                      ))}
                    </select>
                    {salesReps.length === 0 && !salesRepsLoading ? (
                      <p className="text-xs text-rose-600">
                        {t("partners.deals.form.salesRepEmptyHint")}{" "}
                        <Link to="/partners/edit" className="underline">
                          {t("partners.dashboard.editCompany")}
                        </Link>
                      </p>
                    ) : (
                      <p className="text-xs text-slate-500">
                        {t("partners.deals.form.salesRepHint")}
                      </p>
                    )}
                  </div>
                  <Input
                    label={t("partners.deals.form.salesRepPhone")}
                    value={selectedRep?.phone || ""}
                    disabled
                    tabIndex={-1}
                    inputClassName="bg-slate-100 cursor-not-allowed"
                  />
                  <Input
                    label={t("partners.deals.form.salesRepEmail")}
                    value={selectedRep?.email || ""}
                    disabled
                    tabIndex={-1}
                    inputClassName="bg-slate-100 cursor-not-allowed"
                  />
                </div>
              </Section>

              <Section title={t("partners.deals.form.sectionDeal")}>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-slate-700">
                      {t("partners.deals.form.poDate")}
                    </label>
                    <DatePicker
                      className="!h-[38px]"
                      format="YYYY-MM-DD"
                      locale={locale}
                      value={form.poExpectedDate ? dayjs(form.poExpectedDate) : null}
                      onChange={(d) => setField("poExpectedDate", d ? d.format("YYYY-MM-DD") : "")}
                      placeholder={t("partners.deals.form.poDatePlaceholder")}
                    />
                  </div>
                  <Input
                    label={t("partners.deals.form.poQuarter")}
                    value={formatQuarter(autoQuarter) || "-"}
                    disabled
                    tabIndex={-1}
                    inputClassName="bg-slate-100 cursor-not-allowed"
                    hint={t("partners.deals.form.quarterAuto")}
                  />
                  <div className="md:col-span-2">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-medium text-slate-700">
                        {t("partners.deals.form.poAmount")}
                      </label>
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-60 rounded-lg border border-slate-300 px-3 py-2 text-right text-sm tabular-nums focus:border-accent focus:ring-2 focus:ring-accent/30"
                          value={formatAmount(form.poAmount)}
                          onChange={(e) => setField("poAmount", parseAmount(e.target.value))}
                          placeholder={t("partners.deals.form.poAmountPlaceholder")}
                        />
                        <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/40"
                            checked={form.vatExcluded}
                            onChange={(e) => setField("vatExcluded", e.target.checked)}
                          />
                          {t("partners.deals.form.vatExcluded")}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <Textarea
                  className="mt-4"
                  label={t("partners.deals.form.productDetail")}
                  rows={4}
                  maxLength={500}
                  value={form.productDetail}
                  onChange={(e) => setField("productDetail", e.target.value)}
                  placeholder={t("partners.deals.form.productDetailPlaceholder")}
                />

                <Textarea
                  className="mt-4"
                  label={t("partners.deals.form.salesProgress")}
                  rows={8}
                  maxLength={2000}
                  value={form.salesProgress}
                  onChange={(e) => setField("salesProgress", e.target.value)}
                  placeholder={t("partners.deals.form.salesProgressPlaceholder")}
                />

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Input
                    label={t("partners.deals.form.channel")}
                    value={form.channel}
                    onChange={(e) => setField("channel", e.target.value)}
                    maxLength={100}
                  />
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-slate-700">
                      {t("partners.deals.form.poc")}
                    </label>
                    <div className="flex gap-2">
                      {[
                        ["required", t("partners.deals.form.pocRequired")],
                        ["not_required", t("partners.deals.form.pocNotRequired")],
                        ["undecided", t("partners.deals.form.pocUndecided")],
                      ].map(([val, label]) => (
                        <label
                          key={val}
                          className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs ${
                            form.pocRequired === val
                              ? "border-accent bg-accent/10 text-accent"
                              : "border-slate-300 bg-white text-slate-700"
                          }`}
                        >
                          <input
                            type="radio"
                            name="poc"
                            value={val}
                            className="hidden"
                            checked={form.pocRequired === val}
                            onChange={() => setField("pocRequired", val)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <label className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/40"
                    checked={form.brochureDelivered}
                    onChange={(e) => setField("brochureDelivered", e.target.checked)}
                  />
                  {t("partners.deals.form.brochure")}
                </label>
              </Section>

              <div className="flex flex-wrap items-center justify-end gap-3">
                <Link to="/partners/dashboard" className="btw-btn-ghost">
                  {t("partners.deals.detail.cancel")}
                </Link>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btw-btn-accent disabled:opacity-60"
                >
                  {/* '등록' 이 아니라 '심사 요청' — 누르면 심사 대기로 들어가고 관리자
                      승인 후에야 활성 딜이 된다. form.submit 은 관리자 등록·수정 저장과
                      공유하므로 여기서만 쓰는 requestReview* 를 쓴다. */}
                  {submitting
                    ? t("partners.deals.form.requestingReview")
                    : t("partners.deals.form.requestReview")}
                </button>
              </div>
            </form>
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
      <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}
